/**
 * The shared checks of LSEG's date functions (RDP.Date*), ported from
 * Workspace's Excel add-in (Refinitiv for Office 26.30.2): validation texts,
 * labels, dates, tenors, calendar and currency codes.
 *
 * The method names are LSEG's, so the logic can be compared with the source;
 * each carries a `// LSEG: <module>.<method>` pointer. Behaviour the source
 * does not settle is marked [A].
 */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ALLOWED_DATE_FORMATS, CALENDAR_CODES, FX_SOURCES, VALID_CURRENCIES } from "./data.generated";

dayjs.extend(customParseFormat);

/** A validation failure; the message is LSEG's full text. */
export class AdFinValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AdFinValidationError";
  }
}

/** A cell range as Excel passes it: rows of cells. */
export type CellRange = unknown[][];

const CODE_SEPARATORS = /[,;|]/;
// LSEG: AdFinUtils_79658.validateAndReturnCurrencyCode (its minValidCurrencyLength)
const MIN_CURRENCY_LENGTH = 3;

// ---------------------------------------------------------------- strings

// LSEG: toCamelCase_44181.toTitleCase
export function toTitleCase(text: string): string {
  return (text[0] ?? "").toUpperCase() + text.slice(1).toLowerCase();
}

// LSEG: toCamelCase_44181.zn
export function zn(text: string | undefined, width = 2): string {
  return (text ?? "").padStart(width, "0");
}

// ---------------------------------------------------------------- month names

/** Month names of a locale, from Intl, lower-cased and cached. */
// LSEG: MonthNames_35028.MonthNames
export class MonthNames {
  private static cachedLocaleMonthNames: Record<string, { long: string[]; short: string[] }> = {};

  /** The month (0..11) whose long name `name` loosely matches in `locale`, or -1. */
  // LSEG: MonthNames_35028.indexOf
  static indexOf(name: string, locale: string): number {
    const key = this.getPreparedKey(name);
    const loc = this.getPreparedKey(locale);
    if (!key || !loc) return -1;
    const names = this.getOrLoadMonthNames(loc, "long");
    for (let i = 0; i < names.length; i++) if (this.isPartOf(key, names[i])) return i;
    return -1;
  }

  // LSEG: MonthNames_35028.monthsShort
  static monthsShort(locale: string): string[] {
    const loc = this.getPreparedKey(locale);
    return loc ? this.getOrLoadMonthNames(loc, "short") : [];
  }

  // LSEG: MonthNames_35028.getPreparedKey
  private static getPreparedKey(text: string | undefined): string {
    return `${text ?? ""}`.trim().toLocaleLowerCase();
  }

  /** `part` equals `name`, starts it, or shares its first letter and has its other letters in order. */
  // LSEG: MonthNames_35028.isPartOf
  private static isPartOf(part: string, name: string): boolean {
    if (part.length > name.length) return false;
    return (
      part === name ||
      part === name.substring(0, part.length) ||
      (part[0] === name[0] && this.isIncluded(part.substring(1), name.substring(1)))
    );
  }

  // LSEG: MonthNames_35028.isIncluded
  private static isIncluded(letters: string, name: string): boolean {
    let at = -1;
    for (const c of letters) {
      at = name.indexOf(c, at + 1);
      if (at === -1) return false;
    }
    return true;
  }

  // LSEG: MonthNames_35028.loadMonthNames
  private static loadMonthNames(locale: string): { long: string[]; short: string[] } {
    try {
      const long: string[] = [];
      const short: string[] = [];
      const longFmt = new Intl.DateTimeFormat(locale, { month: "long" });
      const shortFmt = new Intl.DateTimeFormat(locale, { month: "short" });
      for (let m = 0; m < 12; m++) {
        short[m] = shortFmt.format(new Date(2023, m, 1)).toLocaleLowerCase();
        long[m] = longFmt.format(new Date(2023, m, 1)).toLocaleLowerCase();
      }
      return { long, short };
    } catch {
      return { long: [], short: [] };
    }
  }

  // LSEG: MonthNames_35028.getOrLoadMonthNames
  private static getOrLoadMonthNames(locale: string, kind: "long" | "short"): string[] {
    let names = this.cachedLocaleMonthNames[locale];
    if (!names) {
      names = this.loadMonthNames(locale);
      this.cachedLocaleMonthNames[locale] = names;
    }
    return names[kind];
  }
}

// LSEG: INVALID_MONTH_NAME_8627.INVALID_MONTH_NAME
export const INVALID_MONTH_NAME = "???";
const DEFAULT_LOCALE = "en-US";

// LSEG: INVALID_MONTH_NAME_8627.getUserLocales
function getUserLocales(): string[] {
  if (typeof window !== "undefined" && window.navigator && window.navigator.languages) {
    return window.navigator.languages.map((l) => `${l}`);
  }
  return [new Intl.NumberFormat().resolvedOptions().locale];
}

/** The month a name stands for, in the user's languages then English, as an `en-US` short name, lower-cased ("oct"). */
// LSEG: INVALID_MONTH_NAME_8627.getDefaultMonthName
export function getDefaultMonthName(name: string, locale?: string): string {
  return MonthNames.monthsShort(DEFAULT_LOCALE)[indexOfMonth(name, locale)] ?? INVALID_MONTH_NAME;
}

// LSEG: INVALID_MONTH_NAME_8627.indexOfMonth
function indexOfMonth(name: string, locale?: string): number {
  for (const loc of locale ? [locale] : getUserLocales()) {
    const i = MonthNames.indexOf(name, loc);
    if (i !== -1) return i;
  }
  return MonthNames.indexOf(name, DEFAULT_LOCALE);
}

// ---------------------------------------------------------------- Excel serials

const EXCEL_ANCHOR_SERIAL = 36963; // 2001-03-13
const EXCEL_ANCHOR_MS = Date.UTC(2001, 2, 13);

// LSEG: getFromExcelDate_78566.getFromExcelDate
function getFromExcelDate(serial: number): Date {
  return new Date(EXCEL_ANCHOR_MS + (Math.floor(serial) - EXCEL_ANCHOR_SERIAL) * 86400000);
}

/**
 * An Excel serial as an ISO date. LSEG's version also reads a few date texts,
 * but only numbers reach it from the date functions, so only they are ported.
 */
// LSEG: mod_30693.formatExcelDate
export function formatExcelDate(serial: number): string {
  if (!serial) throw new Error("Unrecognized date format!");
  const d = getFromExcelDate(serial);
  return `${d.getUTCFullYear()}-${zn(`${d.getUTCMonth() + 1}`)}-${zn(`${d.getUTCDate()}`)}`;
}

// ---------------------------------------------------------------- date texts

/**
 * The shapes a date text is tidied from, before the dayjs formats are tried,
 * each with the group holding a month name (none for all-digit dates). Built
 * with RegExp because `\p{L}` needs the `u` flag, which the ES2017 target
 * does not allow in a literal.
 */
// LSEG: AdFinUtils_79658 (the `p` table)
const IMPROVABLE_DATES: Array<[RegExp, number?]> = [
  [/^(\d{1,4})([\-\/. ])(\d{1,4})([\-\/. ])(\d{1,4})($|\D|T)/],
  [new RegExp(String.raw`^(\p{L}{2,})([\-\/., ]+)(\d{1,4})([\-\/., ]+)(\d{1,4})($|\D|T)`, "u"), 1],
  [new RegExp(String.raw`^(\d{1,4})([\-\/., ]*)(\p{L}{3,})([\-\/., ]*)(\d{1,4})($|\D|T)`, "u"), 3],
  [new RegExp(String.raw`^(\d{1,4})([\-\/., ]+)(\p{L}{2,})([\-\/., ]+)(\d{1,4})($|\D|T)`, "u"), 3],
  [new RegExp(String.raw`^(\d{1,4})([\-\/., ]+)(\d{1,4})([\-\/., ]+)(\p{L}{2,})($|\W)`, "u"), 5],
];

const TENOR_FORMS = [
  /^\-?(\d+Y)?(\d+M)?(\d+W)?(\d+D|\d+WD)?$/,
  /^\-?\d+(D|M|W|WD|Y)$/,
  /^\d+(D|M|W|WD|Y)\/\d+(D|M|W|WD|Y)$/,
  /^(ON|TN|SN|SW|SPOT)$/,
  /^(Odd|Long|IMM|EO[MQY])$/,
  /^\d+x\d+$/,
];

const OPENING_BRACKETS = ["(", "{", "["];
const CLOSING_BRACKETS: Record<string, string> = { ")": "(", "}": "{", "]": "[" };

// LSEG: AdFinUtils_79658.AdFinUtils
export class AdFinUtils {
  /** True when `text` is one or more `symbol`s, spaces allowed ("-", " -- "). */
  // LSEG: AdFinUtils_79658.containsOnlySymbol
  static containsOnlySymbol(text: string, symbol: string): boolean {
    let i = 0;
    let count = 0;
    while (text[i] === symbol || text[i] === " ") {
      if (text[i] === symbol) count++;
      i++;
    }
    return count > 0 && i >= text.length;
  }

  /** Whether `code` is a calendar code; when it is not, throws unless `quiet`. */
  // LSEG: AdFinUtils_79658.isCalendarCode
  isCalendarCode(code: string, asTyped: string, quiet = false): boolean {
    if (code && CALENDAR_CODES.has(code)) return true;
    if (!quiet) this.notValid("Calendar Codes", `provided Calendar Code '${asTyped}' is not correct`);
    return false;
  }

  /** The first allowed dayjs format that reads `text` strictly. */
  // LSEG: AdFinUtils_79658.allowedDateFormat
  allowedDateFormat(text: string): [boolean, string | undefined] {
    for (const formats of Object.values(ALLOWED_DATE_FORMATS)) {
      for (const format of formats) if (dayjs(text, format, true).isValid()) return [true, format];
    }
    return [false, undefined];
  }

  /**
   * Tidies a date text so the strict formats can read it: pads the parts to
   * two digits, puts month names in English ("Oct"), and uses "-" between the
   * parts of a date with a month name. Anything after the date (a time) is dropped.
   */
  // LSEG: AdFinUtils_79658.getImprovedDate
  getImprovedDate<T>(date: T): T | string {
    if (typeof date !== "string") return date;
    const text = date.trim().replace(/\s{2,}/g, " ");
    let shape = 0;
    for (const [re, monthGroup] of IMPROVABLE_DATES) {
      shape++;
      const m = re.exec(text);
      if (!m) continue;
      if (!monthGroup && m[2] !== m[4]) return date;
      const sep = shape === 1 ? m[2] : "-";
      if (monthGroup) m[monthGroup] = toTitleCase(getDefaultMonthName(m[monthGroup]));
      return `${zn(m[1])}${sep}${zn(m[3])}${sep}${zn(m[5])}`;
    }
    return date;
  }

  // LSEG: AdFinUtils_79658.isDateValid
  isDateValid(date: unknown, format?: string): [boolean, string | undefined] {
    if (!date) return [false, undefined];
    if (typeof date === "number") return [true, undefined];
    if (format) return [dayjs(date as string, format, true).isValid(), format];
    return this.allowedDateFormat(date as string);
  }

  /**
   * A date (an Excel serial or a text) as "YYYY-MM-DD". A text that no format
   * reads throws "not in the right format" for `fieldName`, or gives null
   * when no field name is given.
   */
  // LSEG: AdFinUtils_79658.getFormattedDate
  getFormattedDate({ date, format, fieldName }: { date: unknown; format?: string; fieldName?: string }): string | null {
    if (typeof date === "number") return formatExcelDate(date);
    const improved = this.getImprovedDate(date);
    const [valid, used] = this.isDateValid(improved, format);
    if (!valid) {
      if (!fieldName) return null;
      this.notValid(fieldName, "not in the right format");
    }
    return dayjs(improved as string, used, true).startOf("day").format("YYYY-MM-DD");
  }

  // LSEG: AdFinUtils_79658.compareLabel
  compareLabel(a: unknown, b: unknown): boolean {
    return this.fieldNameToLabel(a) === this.fieldNameToLabel(b);
  }

  /** The last opening bracket and where its kind first appears; throws on misplaced brackets. */
  // LSEG: AdFinUtils_79658.getValidatedBracketPos
  getValidatedBracketPos(text: string, fieldName: string): [string, number] {
    let pos = -1;
    let bracket = "";
    const open: string[] = [];
    for (const c of text) {
      if (OPENING_BRACKETS.includes(c)) {
        if (open.length !== 0) this.validate(false, fieldName, "second opening bracket found within the overrides");
        open.push(c);
        pos = text.indexOf(c);
        bracket = c;
      } else if (c in CLOSING_BRACKETS) {
        if (open.length === 0) this.validate(false, fieldName, "an opening bracket must be provided first");
        if (CLOSING_BRACKETS[c] !== open[open.length - 1]) {
          this.validate(false, fieldName, "opening and closing brackets doesn't match");
        }
        open.pop();
      }
    }
    return [bracket, pos];
  }

  /** Strips the first and last characters when `value` starts with `bracket`. */
  // LSEG: AdFinUtils_79658.validateValueForBrackets
  validateValueForBrackets(value: string, bracket: string): string {
    return value && bracket && value.startsWith(bracket) ? value.slice(1, -1) : value;
  }

  /**
   * Splits "label:value" (or "label(value)", "label{value}", ...). Returns
   * [label matches or no label, label as typed, value]; when another label is
   * given, the value is the whole text.
   */
  // LSEG: AdFinUtils_79658.getLabelAndValue
  getLabelAndValue(input: string | null | undefined, label: string, fieldName: string): [boolean, string, string] {
    const text = input?.trim() ?? "";
    if (!text) return [true, "", ""];
    let skip = 1;
    let at = text.indexOf(":");
    const [bracket, bracketAt] = this.getValidatedBracketPos(text, fieldName);
    if (at === -1) at = bracketAt;
    if (bracketAt !== -1 && bracketAt <= at) {
      skip = 0;
      at = bracketAt;
    }
    if (at < 0) return [true, "", text];
    const typedLabel = text.substring(0, at).trim();
    const value = this.validateValueForBrackets(text.substring(at + skip).trim(), bracket);
    const key = typedLabel.replace(/\s/g, "").toLowerCase().trim();
    if (!key) return [true, "", value];
    const matches = key === label.replace(/\s/g, "").toLowerCase();
    return [matches, typedLabel, matches ? value : text];
  }

  // LSEG: AdFinUtils_79658.validate
  validate(condition: unknown, fieldName: string, reason: string): void {
    if (!condition) this.notValid(fieldName, reason);
  }

  // LSEG: AdFinUtils_79658.notValid
  notValid(fieldName: string, reason: string): never {
    throw new AdFinValidationError(`Validation error about argument '${fieldName}': ${reason}!`);
  }

  // LSEG: AdFinUtils_79658.isEmpty
  isEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === "string" && value.trim() === "") return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === "object") {
      for (const _ in value as object) return false;
      return true;
    }
    return false;
  }

  // LSEG: AdFinUtils_79658.validateNotEmpty
  validateNotEmpty(value: unknown, fieldName: string): void {
    this.validate(!this.isEmpty(value), fieldName, "should not be empty");
  }

  /** "3M", "1Y6M", "-2WD", "1M/3M", "ON", "SPOT", "EOM", "3x6", ... */
  // LSEG: AdFinUtils_79658.isTenor
  isTenor(value: unknown): boolean {
    if (!value) return false;
    const text = `${value}`.trim();
    return text !== "-" && TENOR_FORMS.some((re) => re.test(text));
  }

  /** "EMU;fra" -> ["EMU", "FRA"]; "<code>_FI" is accepted for a known code. */
  // LSEG: AdFinUtils_79658.processCalendarCodes
  processCalendarCodes(codes: string): string[] {
    const out: string[] = [];
    codes.split(CODE_SEPARATORS).forEach((asTyped) => {
      const code = `${asTyped}`.trim().toUpperCase();
      if (this.isCalendarCode(code, asTyped, true)) {
        out.push(code);
        return;
      }
      const [base, suffix] = code.split("_", 2);
      if (suffix !== "FI") this.notValid("Calendar Codes", `provided Calendar Code '${asTyped}' is not correct`);
      if (this.isCalendarCode(base, asTyped)) out.push(`${base}_${suffix}`);
    });
    return out;
  }

  /** "EUR; USD" -> ["EUR", "USD"]. Codes are not refused (see validateAndReturnCurrencyCode). */
  // LSEG: AdFinUtils_79658.processCurrencyCodes
  processCurrencyCodes(codes: string): string[] {
    const text = codes.replace(/\s+/g, "");
    if (CODE_SEPARATORS.test(text)) return text.split(CODE_SEPARATORS).map((c) => this.validateAndReturnCurrencyCode(c));
    return [this.validateAndReturnCurrencyCode(text)];
  }

  /**
   * The code when it reads as known currencies end to end ("EURUSD",
   * "DASHEUR"), else `asTyped` (or the code when none is given). A code is
   * never refused here; the API answers for an unknown one. The date
   * functions give no `asTyped`, so they always get the code back; the FX
   * cross code gives the text as the user typed it.
   */
  // LSEG: AdFinUtils_79658.validateAndReturnCurrencyCode
  validateAndReturnCurrencyCode(code: string, asTyped?: string): string {
    const scan = { currencyToCheck: "", validatedCurrencies: "" };
    for (let i = 0; i < code.length; i++) this.validateCurrencies(scan, i, code);
    if (scan.validatedCurrencies === code) return code;
    return asTyped || code;
  }

  /**
   * Adds `code[i]` to the pending letters; from the third letter on, pending
   * letters that are a known currency (of 3, 4 or 6 letters) move to the validated ones.
   */
  // LSEG: AdFinUtils_79658.validateCurrencies
  private validateCurrencies(scan: { currencyToCheck: string; validatedCurrencies: string }, i: number, code: string): void {
    scan.currencyToCheck += code[i];
    if (i > MIN_CURRENCY_LENGTH - 2 && VALID_CURRENCIES.has(scan.currencyToCheck)) {
      scan.validatedCurrencies += scan.currencyToCheck;
      scan.currencyToCheck = "";
    }
  }

  /** The entries whose value is truthy. */
  // LSEG: AdFinUtils_79658.getObjectWithNonEmptyValue
  getObjectWithNonEmptyValue<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const out: Partial<T> = {};
    for (const key of Object.keys(obj) as (keyof T)[]) if (obj[key]) out[key] = obj[key];
    return out;
  }

  // LSEG: AdFinUtils_79658.validateLabel
  validateLabel(label: unknown, fieldName: string): void {
    this.validate(this.compareLabel(label, fieldName), fieldName, `Provided a wrong '${fieldName}' argument`);
  }

  /** " Start Date " -> "startdate". */
  // LSEG: AdFinUtils_79658.fieldNameToLabel
  fieldNameToLabel(name: unknown): string {
    return `${name ?? ""}`.replace(/\s+/g, "").toLowerCase();
  }

  /** ["StartDate"] -> { startdate: "StartDate" }. */
  // LSEG: AdFinUtils_79658.listToObject
  listToObject(names: readonly string[]): Record<string, string> {
    const out: Record<string, string> = {};
    names.forEach((n) => {
      out[this.fieldNameToLabel(n)] = n;
    });
    return out;
  }
}

/** Whether `value` holds a digit anywhere ("1.1", "1M", "D3"): LSEG's test for a number. */
// LSEG: AFCellRangeProcessor_29882.isNumber (and SpotCellRangeProcessor_64954.isNumber)
export function isNumber(value: unknown): boolean {
  return !!value && /(\d+(?:\.\d+)?)/.test(`${value}`);
}

/** Whether `key` is one of LSEG's FX source codes, as its `sources.hasOwnProperty(key)` reads it. */
// LSEG: sources_50612.sources (hasOwnProperty)
export function isFxSource(key: unknown): boolean {
  return FX_SOURCES.has(`${key}`);
}

/** `obj` without its falsy values and empty arrays, as LSEG's clean-up loops leave it. */
// LSEG: the clean-up loop of ForexUtils_75582.getSpotCcy / getForexOverrides,
// AFCellRangeProcessor_29882.processCellRangeSwapPointsCcy, ZcCurveCellRangeProcessor_28099.cleanZcCurveObject
// and ZcToFraProcessor_93238.clearEmptyZcCurveValues
export function withoutEmptyValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const key of Object.keys(obj) as (keyof T)[]) {
    const value = obj[key];
    if (value && !(Array.isArray(value) && value.length === 0)) out[key] = value;
  }
  return out;
}
